import { Injectable } from '@angular/core';
import { Firestore, collection, doc, collectionData, docData, query as firestoreQuery, getDocs, setDoc, deleteDoc, updateDoc, addDoc, Timestamp, QueryConstraint, QuerySnapshot, CollectionReference, DocumentReference } from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface FirestoreDocTemplate {
  docId?: string;
  creationTimestamp?: Timestamp;
  updateTimestamp?: Timestamp;
}

@Injectable()

export abstract class FirestoreService<T extends { docId?: string }> {
  protected abstract basePath: string;
  constructor(protected firestore: Firestore) {}

  private collectionRef() {
    return collection(this.firestore, this.basePath) as CollectionReference<T>;
  }

  private docRef(id: string) {
    return doc(this.firestore, `${this.basePath}/${id}`) as DocumentReference<T>;
  }
  doc$(id: string): Observable<T | undefined> {
    return docData(this.docRef(id), { idField: 'docId' }).pipe(
      map(data => data as T),
      tap(r => {
        if (!environment.production) {
          console.groupCollapsed(`Firestore Streaming [${this.basePath}] [doc$] ${id}`);
          console.log(r);
          console.groupEnd();
        }
      })
    );
  }

  collection$(constraints: QueryConstraint[] = []): Observable<T[]> {
    const q = constraints.length ? firestoreQuery(this.collectionRef(), ...constraints) : this.collectionRef();
    return collectionData(q, { idField: 'docId' }).pipe(
      map(arr => arr as T[]),
      tap(r => {
        if (!environment.production) {
          console.groupCollapsed(`Firestore Streaming [${this.basePath}] [collection$]`);
          console.table(r);
          console.groupEnd();
        }
      })
    );
  }

  collectionSnapshot$(constraints: QueryConstraint[] = []): Observable<{ id: string, data: T }[]> {
    const q = constraints.length ? firestoreQuery(this.collectionRef(), ...constraints) : this.collectionRef();
    return collectionData(q, { idField: 'docId' }).pipe(
      map(arr => (arr as T[]).map(data => ({ id: (data as any).docId, data }))),
      tap(r => {
        if (!environment.production) {
          console.groupCollapsed(`Firestore Streaming [${this.basePath}] [collectionSnapshot$]`);
          r.forEach((snapshot) => {
            console.log('id', snapshot.id);
            console.log('data', snapshot.data);
          });
          console.groupEnd();
        }
      })
    );
  }

  // Pega apenas um snapshot
  // EX:
  // this.profile.collectionGet().pipe(first()).subscribe((snapshots) => {
  //   snapshots.docs.forEach((doc) => {
  //     console.log(doc.data())
  //   })
  // });

  collectionGet(constraints: QueryConstraint[] = []): Observable<QuerySnapshot<T>> {
    const q = constraints.length ? firestoreQuery(this.collectionRef(), ...constraints) : this.collectionRef();
    // Cast to QuerySnapshot<T> for compatibility
    return from(getDocs(q) as Promise<QuerySnapshot<T>>).pipe(
      tap(r => {
        if (!environment.production) {
          console.groupCollapsed(`Firestore Query [${this.basePath}] [collectionGet]`);
          r.docs.forEach((doc) => {
            console.log(doc.id);
            console.log(doc.data());
          });
          console.groupEnd();
        }
      })
    );
  }

  async create(value: T, docID?: string) {
    let id: string;
    if (docID) {
      id = docID;
    } else {
      // Generate a random doc ID
      id = doc(collection(this.firestore, this.basePath)).id;
    }
    const creationTimestamp = Timestamp.now();
    await setDoc(this.docRef(id), { ...value, creationTimestamp }, { merge: true });
    if (!environment.production) {
      console.groupCollapsed(`Firestore Service [${this.basePath}] [create]`);
      console.log('[Id]', id, value);
      console.groupEnd();
    }
    return id;
  }

  async updateTimestamp(value: Partial<T>, docID: string) {
    const updateTimestamp = Timestamp.now();
    await setDoc(this.docRef(docID), { ...value, updateTimestamp }, { merge: true });
    if (!environment.production) {
      console.groupCollapsed(`Firestore Service [${this.basePath}] [update]`);
      console.log('[Id]', docID, value);
      console.groupEnd();
    }
  }

  async update(value: Partial<T>, docID: string) {
    await setDoc(this.docRef(docID), { ...value }, { merge: true });
    if (!environment.production) {
      console.groupCollapsed(`Firestore Service [${this.basePath}] [update]`);
      console.log('[Id]', docID, value);
      console.groupEnd();
    }
  }

  async delete(id: string) {
    await deleteDoc(this.docRef(id));
    if (!environment.production) {
      console.groupCollapsed(`Firestore Service [${this.basePath}] [delete]`);
      console.log('[Id]', id);
      console.groupEnd();
    }
  }

  // Função recursiva que remove undefined dos objetos
  // EFEITO COLATERAL: Transforma vetores em mapas
  private removeEmpty = (obj: any) => {
    const newObj: any = {};
    Object.keys(obj).forEach((key) => {
      if (obj[key] === Object(obj[key])) { newObj[key] = this.removeEmpty(obj[key]); }
      else if (obj[key] !== undefined) { newObj[key] = obj[key]; }
    });
    return newObj;
  };
}
